/**
 * Unified Spinora loading spinner — used by LoadingScreen and Suspense fallback.
 * Single consistent visual prevents "smaller logo" glitch when transitioning between loaders.
 * On mobile/PWA: simplified animations to prevent GPU glitching (no pulse, no will-change, simpler dots).
 * Logo has onError + timeout fallback to prevent indefinite loading when image 404s or hangs.
 */
import React, { useState, useEffect, useRef } from 'react';
import { useMobile } from '../hooks/useMobile';
import { isPWA } from '../utils/capacitor';

const LOGO_LOAD_TIMEOUT_MS = 2500;

export const AppLoadingSpinner: React.FC = () => {
  const isMobileViewport = useMobile();
  const isMobileOrPwa = isMobileViewport || isPWA();
  const [logoFailed, setLogoFailed] = useState(false);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const t = setTimeout(() => {
      if (imgRef.current && !imgRef.current.complete) setLogoFailed(true);
    }, LOGO_LOAD_TIMEOUT_MS);
    return () => clearTimeout(t);
  }, []);

  return (
    <div
      className="flex flex-col items-center justify-center space-y-6"
      data-loading-spinner
      style={{
        transform: 'translate3d(0,0,0)',
        contain: 'layout style paint',
        isolation: 'isolate',
        WebkitBackfaceVisibility: 'hidden' as const,
        backfaceVisibility: 'hidden' as const,
      }}
    >
      {!logoFailed ? (
        <img
          ref={imgRef}
          src="/SpinoraWhiteLogo.png"
          alt="Spinora"
          className="h-16 w-auto"
          data-loading-logo
          onError={() => setLogoFailed(true)}
          style={{
            animation: isMobileOrPwa ? 'none' : 'spinoraPulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
            transform: 'translate3d(0,0,0)',
            WebkitBackfaceVisibility: 'hidden' as const,
            backfaceVisibility: 'hidden' as const,
          }}
        />
      ) : (
        <span className="h-16 flex items-center text-white font-bold text-xl tracking-wide" data-loading-logo-fallback>Spinora</span>
      )}
      <div className="flex items-center space-x-2" aria-hidden style={{ transform: 'translate3d(0,0,0)' }}>
        <div
          className="w-2 h-2 bg-white rounded-full"
          style={
            isMobileOrPwa
              ? { animation: 'spinoraDotMobile 1.2s ease-in-out infinite both', animationDelay: '0s' }
              : {
                  animation: 'spinoraBounce 1.4s ease-in-out infinite both',
                  animationDelay: '0s',
                  willChange: 'transform, opacity',
                }
          }
        />
        <div
          className="w-2 h-2 bg-white rounded-full"
          style={
            isMobileOrPwa
              ? { animation: 'spinoraDotMobile 1.2s ease-in-out infinite both', animationDelay: '0.2s' }
              : {
                  animation: 'spinoraBounce 1.4s ease-in-out infinite both',
                  animationDelay: '0.2s',
                  willChange: 'transform, opacity',
                }
          }
        />
        <div
          className="w-2 h-2 bg-white rounded-full"
          style={
            isMobileOrPwa
              ? { animation: 'spinoraDotMobile 1.2s ease-in-out infinite both', animationDelay: '0.4s' }
              : {
                  animation: 'spinoraBounce 1.4s ease-in-out infinite both',
                  animationDelay: '0.4s',
                  willChange: 'transform, opacity',
                }
          }
        />
      </div>
      <style>{`
        @keyframes spinoraPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
        @keyframes spinoraBounce {
          0%, 80%, 100% { transform: scale(0); opacity: 0.5; }
          40% { transform: scale(1); opacity: 1; }
        }
        @keyframes spinoraDotMobile {
          0%, 100% { opacity: 0.4; }
          50% { opacity: 1; }
        }
      `}</style>
    </div>
  );
};
